import { useIntl } from 'react-intl'

import { useAllocateTransportRequest } from '@/api/useAllocateTransportRequest.ts'
import { useAssignTransportRequest } from '@/api/useAssignTransportRequest.ts'
import { useCancelTransportRequest } from '@/api/useCancelTransportRequest.ts'
import { useCompleteTransportRequest } from '@/api/useCompleteTransportRequest.ts'
import { useCustomerNotFound } from '@/api/useCustomerNotFound.ts'
import { useOnboardTransportRequest } from '@/api/useOnboardTransportRequest.ts'
import { usePutOnHoldTransportRequest } from '@/api/usePutOnHoldTransportRequest.ts'
import { useReactivateTransportRequest } from '@/api/useReactivateTransportRequest.ts'
import { useToggleEveningMobileEnabled } from '@/api/useToggleEveningMobileEnabled.ts'
import { useUpdateAssignmentType } from '@/api/useUpdateAssignmentType.ts'
import { useUpdateTransportRequestDonation } from '@/api/useUpdateTransportRequestDonation.ts'
import { useUpdateWaitTimeType } from '@/api/useUpdateWaitTimeType.ts'
import { useToast } from '@/components/molecules/Toaster/useToast.ts'
import { type MapActions } from '@/components/organisms/Map/Map.tsx'
import { type AppViewAction } from '@/components/templates/AppView'
import { useRegion } from '@/model/contexts/useRegion.ts'
import { useBackToPilote } from '@/pages/actions/useBackToPilote.ts'

import { type AppPageStateActions } from '../AppPageStateProvider.tsx'

export const useAppActions = (): Omit<AppViewAction, AppPageStateActions | keyof MapActions | 'onHighlight'> => {
  const {
    region: { _id: regionId, waitTimeConfiguration },
  } = useRegion()

  const intl = useIntl()
  const { toast } = useToast()

  const onApiError = (e: unknown) => {
    // eslint-disable-next-line no-console
    console.log('error', e)
    toast({
      title: intl.formatMessage({ id: 'toast.error.title' }),
      description: intl.formatMessage({ id: 'toast.error.description' }),
      variant: 'destructive',
    })
  }

  const assignTransportRequest = useAssignTransportRequest({ onError: onApiError })
  const allocateTransportRequest = useAllocateTransportRequest({ onError: onApiError })
  const onboardTransportRequest = useOnboardTransportRequest({ onError: onApiError })
  const customerNotFound = useCustomerNotFound({ onError: onApiError })
  const reactivateTransportRequest = useReactivateTransportRequest({ onError: onApiError })

  const cancelTransportRequest = useCancelTransportRequest({ onError: onApiError })
  const putOnHoldTransportRequest = usePutOnHoldTransportRequest({ onError: onApiError })
  const completeTransportRequest = useCompleteTransportRequest({ onError: onApiError })
  const updateTransportRequestDonation = useUpdateTransportRequestDonation({ onError: onApiError })

  const toggleEveningMobileEnabled = useToggleEveningMobileEnabled({ onError: onApiError })
  const updateWaitTimeType = useUpdateWaitTimeType({ onError: onApiError })
  const updateAssignmentType = useUpdateAssignmentType({ onError: onApiError })
  const onBackToPiloteClicked = useBackToPilote()

  return {
    onBackToPiloteClicked,
    onAssignTeam: (transportRequestId, teamId) => {
      assignTransportRequest.mutate({ regionId, teamId, transportRequestId })
    },
    onAllocateManually: (transportRequestId) => {
      allocateTransportRequest.mutate({ regionId, transportRequestId })
    },
    onCustomerTakenCareOf: (transportRequestId) => {
      onboardTransportRequest.mutate({ regionId, transportRequestId })
    },
    onReactivate: (transportRequestId) => {
      reactivateTransportRequest.mutate({ regionId, transportRequestId })
    },
    onCancelPendingRequest: (transportRequestId) => {
      cancelTransportRequest.mutate({ regionId, transportRequestId })
    },
    onCancelRequest: (transportRequestId, teamStatus) => {
      cancelTransportRequest.mutate({ regionId, transportRequestId, teamStatus })
    },
    onPutOnHold: (transportRequestId, teamStatus) => {
      putOnHoldTransportRequest.mutate({ regionId, transportRequestId, teamStatus })
    },
    onCustomerNotFound: (transportRequestId, teamStatus) => {
      customerNotFound.mutate({ regionId, transportRequestId, teamStatus })
    },
    onArrivedAtDestination: (transportRequestId, teamStatus, donation) => {
      completeTransportRequest.mutate({ regionId, transportRequestId, teamStatus, donation })
    },
    onChangeDonation: (transportRequestId, donation) => {
      updateTransportRequestDonation.mutate({ regionId, transportRequestId, donation })
    },
    onMobileRequestToggled: () => toggleEveningMobileEnabled.mutateAsync({ regionId }),
    onWaitTimeToggled: () =>
      updateWaitTimeType.mutateAsync({
        regionId,
        value: waitTimeConfiguration.type === 'default' ? 'increased' : 'default',
      }),
    onChangeAssignationState: async (state) =>
      updateAssignmentType.mutateAsync({
        regionId,
        value: state,
      }),
  }
}
